/**
 * Community Post Detail Route
 */
import { createFileRoute } from '@tanstack/react-router';
import { PostDetailTemplatePending } from '@/components/v2/page-template-pending';
import type { PostDetailV2 } from '@/lib/api/v2/community';
import { cacheFirst } from '@/lib/queries/cache-first';
import { communityPostQuery } from '@/lib/queries/detail';
import { findCommunityPostPreview } from '@/lib/community/post-preview';

export type PostDetailResult<T> = { ok: true; data: T } | { ok: false };
const toPostDetailResult = async <T,>(promise: Promise<T>): Promise<PostDetailResult<T>> => {
  try {
    return { ok: true, data: await promise };
  } catch {
    return { ok: false };
  }
};

export const Route = createFileRoute('/_authenticated/community/posts/$postId')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  pendingComponent: PostDetailTemplatePending,
  loader: ({ context, params, preload }) => {
    // Key + queryFn come from the shared factory (app/lib/queries/detail.ts):
    // same key + shape the detail page's useQuery consumes
    // (post-detail-page.tsx); authenticatedGetShared picks the cookie-forwarding
    // server fn on SSR and the direct Go API call on client navigations (perf
    // audit part 2, Theme 1).
    const postQuery = communityPostQuery(params.postId);
    const refreshPost = () => context.queryClient.fetchQuery(postQuery);
    // The feed-card preview rides along in every branch: it is a pure cache
    // read (no network) that lets the pending skeleton paint real content.
    const preview = findCommunityPostPreview(context.queryClient, params.postId);
    // Preload short-circuit: detail-entry cards fire this loader via
    // <Link preload="viewport"> as they scroll past, and a preload run must
    // never touch the network. Serve the cache when present, else an
    // instantly-resolved cold ({ ok: false }) envelope. The result must
    // SETTLE, never park: preloadRoute keeps the loader lane, its abort
    // controller and match resources alive until the loader settles, so a
    // never-resolving promise would leak a permanently-pending lane for
    // every card scrolled past. The ok:false value is never rendered —
    // commits always re-run this loader (defaultPreloadStaleTime is 0);
    // warming the route chunk is all a preload exists for here.
    // getQueryData is optional-chained for the loader-contract tests, which
    // stub the client with ensureQueryData only; a missing reader simply
    // reads as cold cache.
    if (preload) {
      const cached = context.queryClient.getQueryData?.<PostDetailV2>(postQuery.queryKey);
      return {
        preview,
        postDetailPromise:
          cached === undefined
            ? Promise.resolve<PostDetailResult<PostDetailV2>>({ ok: false })
            : toPostDetailResult(Promise.resolve(cached)),
      };
    }
    // Cache-first gate: the shared gate (app/lib/queries/data-plane, through
    // cacheFirst) owns the freshness decision, so this loader and the
    // prefetchers can never disagree about whether the cache entry is usable.
    // The preview above only decorates the skeleton, so a cached post resolves
    // the envelope immediately and a stale entry is healed in the background
    // (fetchQuery, single-flight-deduped with the detail page's mounted
    // query); a cold cache keeps the streamed fetch below, with its existing
    // retry/error paths.
    const { cached, stream } = cacheFirst(context.queryClient, postQuery.queryKey, refreshPost);
    return {
      preview,
      postDetailPromise:
        cached !== undefined
          ? toPostDetailResult(Promise.resolve(cached))
          : toPostDetailResult(stream()),
    };
  },
  headers: () => ({ 'Cache-Control': 'private, no-store' }),
});
